'use client';

/* eslint-disable no-underscore-dangle */
import React, { useMemo } from 'react';
import { useHits } from 'react-instantsearch';
import { useAtom } from 'jotai';
import ProductCardGridUI from 'components/consumer/ProductCardGridUI';
import ProductCardStatic from 'components/consumer/ProductCardStatic';
import ProductCardStaticPlaceholder from 'components/consumer/ProductCardStaticPlaceholder';
import useProductCardDataNext from 'hooks/useProductCardDataNext';
import { createSearchProductClickHandler } from 'components/consumer/consumer-search/algolia-insights-helpers/createSearchProductClickHandler';
import lastInsightsClickAtom from 'components/consumer/consumer-search/advanced-search/atoms/lastInsightsClickAtom';
import { getVariantsByMatchedColorFirst } from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits/utils';

/**
 * Search Modal Product Grid component
 * Uses GraphQL data for full product details with images and prices
 * Makes one batch GraphQL call for all products
 */
const SearchModalProductGridNext = () => {
  const { hits } = useHits();
  const [, setLastInsightsClick] = useAtom(lastInsightsClickAtom);

  const limitedHits = useMemo(() => hits.slice(0, 3), [hits]);

  const productIds = useMemo(
    () => limitedHits.map(hit => parseInt(hit.product_id || hit.objectID, 10)),
    [limitedHits]
  );

  // Fetch and process product data
  const { products, loading } = useProductCardDataNext({ productIds });

  const productsWithHighlightedVariantsFirst = useMemo(
    () =>
      products.map(product => ({
        ...product,
        variantsToShow: getVariantsByMatchedColorFirst(product, hits),
      })),
    [products, hits]
  );

  const productMap = useMemo(
    () =>
      Object.fromEntries(
        (productsWithHighlightedVariantsFirst ?? []).map(product => [
          product.id,
          product,
        ])
      ),
    [productsWithHighlightedVariantsFirst]
  );

  if (!limitedHits || limitedHits.length === 0) {
    return (
      <div
        className="text-center py-8 text-gray-500"
        role="status"
        aria-live="polite"
      >
        No products found
      </div>
    );
  }

  if (loading) {
    return (
      <ProductCardGridUI
        columnsOnDesktop="3"
        columnsOnMobile="2"
        limitWidth={false}
      >
        {limitedHits.map(hit => (
          <ProductCardStaticPlaceholder key={hit.objectID} />
        ))}
      </ProductCardGridUI>
    );
  }

  return (
    <ProductCardGridUI
      columnsOnDesktop="3"
      columnsOnMobile="2"
      limitWidth={false}
    >
      {limitedHits.map((hit, index) => {
        const productId = parseInt(hit.product_id || hit.objectID, 10);
        const product = {
          ...productMap[productId],
          name: hit?.name ?? productMap[productId]?.name ?? '',
        };

        if (!product || !product.id) {
          return <ProductCardStaticPlaceholder key={hit.objectID} />;
        }

        const algoliaMetaData = {
          objectID: hit.objectID ?? String(productId),
          queryID: hit.__queryID ?? '',
          position: index + 1,
        };

        return (
          <ProductCardStatic
            key={hit.objectID}
            {...product}
            position={index + 1}
            layout="minimal"
            analyticsComponentName="search-product-card"
            onClick={createSearchProductClickHandler(
              algoliaMetaData,
              setLastInsightsClick,
              { hit, productId }
            )}
          />
        );
      })}
    </ProductCardGridUI>
  );
};

export default SearchModalProductGridNext;
